import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QuickchatAiIcon } from '@/Domain/components/icons/QuickchatAiWordmark.icon';

export const BannerShopify = () => {
  const { t } = useTranslation('banner_shopify');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
      videoRef.current.play().catch(() => {});

      // Add event listeners to track play/pause state
      const video = videoRef.current;
      const handlePlay = () => setIsPaused(false);
      const handlePause = () => setIsPaused(true);

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      };
    }
  }, []);

  const handleVideoClick = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
  };

  const handleMuteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // don’t trigger play/pause
    if (!videoRef.current) return;
    const next = !isMuted;
    videoRef.current.muted = next;
    setIsMuted(next);
  };

  return (
    <div className="w-full relative px-10 py-12 flex flex-col items-center justify-center gap-16 bg-slate-50">
      <div className="flex-row flex items-center justify-center gap-4">
        <QuickchatAiIcon className="scale-150" /> +
        <img
          src="http://quickchat.ai/assets/shopify-logo_1uanJs.webp"
          alt="Shopify Logo"
          className="h-10 w-auto mb-2"
        />
      </div>

      <div className="flex flex-col items-center">
        <h1 className="text-foreground text-2xl font-medium mb-2">{t('title')} &#129782;</h1>
        <p className="text-foreground-subtle">{t('description')}</p>
      </div>

      <div
        className="relative rounded-lg w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <video
          ref={videoRef}
          className="w-full shadow-2xl rounded-xl cursor-pointer"
          src="https://storage.googleapis.com/quickchat-files/appquickchat/media/short-install.mp4"
          autoPlay
          loop
          muted={isMuted}
          playsInline
          onClick={handleVideoClick}
        />

        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 pointer-events-none z-10 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        >
          <button
            onClick={handleVideoClick}
            className="bg-black/50 text-white p-4 rounded-full hover:bg-black/70 transition pointer-events-auto flex items-center justify-center"
            aria-label={isPaused ? 'Play video' : 'Pause video'}
          >
            {isPaused ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                className="bi bi-play-fill"
                viewBox="0 0 16 16"
              >
                <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                className="bi bi-pause-fill"
                viewBox="0 0 20 16"
              >
                <path d="M5.5 3.5A1.5 1.5 0 0 1 7 2h1a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 8 14H7a1.5 1.5 0 0 1-1.5-1.5v-9zm5 0A1.5 1.5 0 0 1 12 2h1a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 13 14h-1a1.5 1.5 0 0 1-1.5-1.5v-9z" />
              </svg>
            )}
          </button>
        </div>

        <button
          onClick={handleMuteClick}
          className="absolute bottom-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
        >
          {isMuted ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-volume-mute-fill"
              viewBox="0 0 16 16"
            >
              <path d="M6.717 3.55A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.812.39L3.825 10.5H1.5A.5.5 0 0 1 1 10V6a.5.5 0 0 1 .5-.5h2.325l2.363-1.89a.5.5 0 0 1 .529-.06m7.137 2.096a.5.5 0 0 1 0 .708L12.207 8l1.647 1.646a.5.5 0 0 1-.708.708L11.5 8.707l-1.646 1.647a.5.5 0 0 1-.708-.708L10.793 8 9.146 6.354a.5.5 0 1 1 .708-.708L11.5 7.293l1.646-1.647a.5.5 0 0 1 .708 0" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-volume-up-fill"
              viewBox="0 0 16 16"
            >
              <path d="M11.536 14.01A8.47 8.47 0 0 0 14.026 8a8.47 8.47 0 0 0-2.49-6.01l-.708.707A7.48 7.48 0 0 1 13.025 8c0 2.071-.84 3.946-2.197 5.303z" />
              <path d="M10.121 12.596A6.48 6.48 0 0 0 12.025 8a6.48 6.48 0 0 0-1.904-4.596l-.707.707A5.48 5.48 0 0 1 11.025 8a5.48 5.48 0 0 1-1.61 3.89z" />
              <path d="M8.707 11.182A4.5 4.5 0 0 0 10.025 8a4.5 4.5 0 0 0-1.318-3.182L8 5.525A3.5 3.5 0 0 1 9.025 8 3.5 3.5 0 0 1 8 10.475zM6.717 3.55A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.812.39L3.825 10.5H1.5A.5.5 0 0 1 1 10V6a.5.5 0 0 1 .5-.5h2.325l2.363-1.89a.5.5 0 0 1 .529-.06" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};
